/**
 * Phát hiện web quầy có bản mới (docs/plans/nut-cap-nhat-ban-moi-web-quay.md, mục 2) — phần thuần, không React.
 *
 * Vì sao: tablet ở quầy mở cả ngày nên chạy bản cũ (kể cả lỗi đã sửa), PWA đã cài lại không có nút tải lại.
 *
 * "Vân tay asset": mỗi bản dựng Vite đặt tên file có mã băm (`/assets/main-XXXX.js`), nên danh sách đường dẫn
 * `/assets/...` trong `index.html` đổi theo từng bản. So vân tay của bản đang chạy (lấy MỘT lần lúc khởi động) với
 * vân tay `index.html` trên máy chủ. Không dùng `updatefound` vì `sw.js` không đổi theo từng bản dựng.
 *
 * Chống báo nhầm (lúc đang deploy / lùi bản / mạng lỗi): chỉ báo khi HAI lần liền nhau, cách ≥ 60 giây, cùng ra một
 * vân tay khác bản đang chạy. Lỗi mạng / hết giờ / không phải 2xx / HTML không có `/assets/` → bỏ qua, không hiện lỗi.
 *
 * Máy ngủ / app bị tạm dừng (iOS): `performance.now()` có thể không tính thời gian đó. Nên khi tab đổi từ ẩn sang hiện, lần kiểm
 * ĐẦU được bỏ luật "cách ≥ 60 giây" (vẫn không chồng yêu cầu); lần xác nhận thứ hai đo bằng HẸN GIỜ (60 giây thật), không bằng đồng hồ đo.
 * Ngoài ra service worker v4 (public/sw.js) đẩy thông điệp `co-ban-moi` tới tab đang hiện khi nó đổi bản (ADR-119).
 *
 * Thời gian chỉ đo bằng `performance.now()` qua `dongHo` tiêm vào (không dùng đồng hồ treo tường): đây là khoảng cách giữa hai
 * lần kiểm, không phải mốc nghiệp vụ.
 */
import { GIOI_HAN_GET_MS, type Hen, type NguonSuKien } from './tu-phuc-hoi.ts'

/** Hỏi máy chủ mỗi chừng này khi tab đang hiện. */
export const CHU_KY_KIEM_MS = 300_000
/** Hai lần hỏi liên tiếp (kể cả do `online` / tab hiện lại) cách nhau ít nhất chừng này. */
export const GIAN_CACH_TOI_THIEU_MS = 60_000
/** Thấy vân tay lạ lần đầu thì hỏi lại sau chừng này để chắc chắn. */
export const CHO_XAC_NHAN_MS = 60_000

/** Một đường dẫn `/assets/...` trong thuộc tính `src` / `href` của thẻ `<script>` hoặc `<link>`. */
const THE_ASSET =
  /<(?:script|link)\b[^>]*?\b(?:src|href)\s*=\s*(?:"(\/assets\/[^"]*)"|'(\/assets\/[^']*)')/gi

/**
 * Danh sách đã sắp xếp mọi đường dẫn `/assets/...` trong `<script src>` và `<link href>`, nối bằng `|`.
 * Không có đường dẫn nào → `null` (trang lỗi, trang đăng nhập wifi…: không so sánh được).
 */
export function layVanTay(html: string): string | null {
  const ra: string[] = []
  for (const m of html.matchAll(THE_ASSET)) {
    const d = m[1] ?? m[2]
    if (d !== undefined) ra.push(d)
  }
  if (ra.length === 0) return null
  return [...new Set(ra)].sort().join('|')
}

/** Kết quả một lần hỏi máy chủ: lấy được (kèm vân tay, có thể null) hay lỗi. */
export type LanLay = { ok: true; vanTay: string | null } | { ok: false }

export interface KetQuaBuoc {
  /** Vân tay lạ đang chờ lần xác nhận thứ hai (null: không có). */
  ungVien: string | null
  /** Hai lần liên tiếp cùng ra một vân tay lạ: hiện dải. */
  hien: boolean
}

/**
 * Một bước quyết định (hàm thuần). `chay`: vân tay bản đang chạy; `truoc`: ứng viên của lần trước.
 * - `chay` null → không bao giờ báo.
 * - Lỗi / vân tay null → huỷ ứng viên, giữ nguyên.
 * - Trùng bản đang chạy → huỷ ứng viên.
 * - Lạ và trùng ứng viên trước → hiện. Lạ và khác ứng viên → ứng viên mới.
 */
export function buocKiem(chay: string | null, truoc: string | null, lan: LanLay): KetQuaBuoc {
  if (chay === null) return { ungVien: null, hien: false }
  if (!lan.ok || lan.vanTay === null) return { ungVien: null, hien: false }
  if (lan.vanTay === chay) return { ungVien: null, hien: false }
  if (truoc !== null && lan.vanTay === truoc) return { ungVien: truoc, hien: true }
  return { ungVien: lan.vanTay, hien: false }
}

/** Phần của `fetch` mà ta cần — đủ nhỏ để test dựng giả. */
export type HamFetch = (
  url: string,
  init: { cache: 'no-store'; credentials: 'same-origin'; signal: AbortSignal | null },
) => Promise<{ ok: boolean; text(): Promise<string> }>

export interface MoiTruongBanMoi {
  /** Vân tay bản đang chạy, lấy MỘT lần lúc khởi động. `null` → không kiểm gì cả. */
  vanTayChay: string | null
  hen: Hen
  cuaSo: NguonSuKien
  taiLieu: NguonSuKien & { readonly visibilityState: string }
  /** `performance.now()` — đo khoảng cách giữa hai lần kiểm. */
  dongHo: () => number
  fetch: HamFetch
  /** Gọi đúng một lần khi chắc chắn có bản mới; sau đó bộ kiểm dừng hẳn. */
  khiCoBanMoi: () => void
}

/** Hạn chót cho một yêu cầu: dùng `AbortSignal.timeout` nếu trình duyệt có, không thì không đặt (máy rất cũ). */
function hanChot(): AbortSignal | null {
  return typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
    ? AbortSignal.timeout(GIOI_HAN_GET_MS)
    : null
}

/** Hỏi `index.html` trên máy chủ MỘT lần (không qua cache). Lỗi mạng / không 2xx → `{ ok: false }`, không ném. */
export async function layVanTayMayChu(f: HamFetch): Promise<LanLay> {
  try {
    const res = await f('/index.html', {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: hanChot(),
    })
    if (!res.ok) return { ok: false }
    return { ok: true, vanTay: layVanTay(await res.text()) }
  } catch {
    return { ok: false }
  }
}

/**
 * Service worker mới vừa nhận quyền điều khiển và báo `co-ban-moi` (sw.js v4): kiểm MỘT lần, tức thì, xem bản trên máy chủ có
 * khác bản đang chạy không. Không tin thông điệp mù quáng: máy vừa tải trang mới (cùng vân tay) thì không được hiện dải oan.
 * Trả `true` chỉ khi chắc chắn khác (vân tay máy chủ có, khác bản đang chạy).
 */
export async function xacNhanSauThongBao(chay: string | null, f: HamFetch): Promise<boolean> {
  if (chay === null) return false
  const lan = await layVanTayMayChu(f)
  return lan.ok && lan.vanTay !== null && lan.vanTay !== chay
}

/** Phần của `Document` mà `coViecDoDang` cần (để test không cần trình duyệt). */
export interface TaiLieuDoDang {
  readonly visibilityState: string
  readonly activeElement: { readonly tagName: string } | null
  querySelector(chon: string): unknown
  querySelectorAll(chon: string): Iterable<{ tagName: string; type?: string; value?: string }>
}

/** Loại `<input>` không chứa chữ người gõ. */
const INPUT_KHONG_PHAI_CHU = new Set([
  'hidden',
  'button',
  'submit',
  'reset',
  'checkbox',
  'radio',
  'file',
  'image',
  'range',
  'color',
])

/**
 * Người dùng đang dở việc? (tờ đang mở, con trỏ đang ở ô nhập, hoặc đã gõ chữ vào một ô). Cố ý THẬN TRỌNG: ô có chữ là coi như đang dở
 * (không so với `defaultValue` vì React đồng bộ thuộc tính `value` của ô điều khiển nên `defaultValue` luôn bằng giá trị hiện tại).
 */
export function coViecDoDang(doc: TaiLieuDoDang): boolean {
  if (doc.querySelector('[aria-modal="true"]') !== null) return true
  const dangFocus = doc.activeElement?.tagName
  if (dangFocus === 'INPUT' || dangFocus === 'TEXTAREA' || dangFocus === 'SELECT') return true
  for (const o of doc.querySelectorAll('input, textarea')) {
    if (o.tagName === 'INPUT' && INPUT_KHONG_PHAI_CHU.has((o.type ?? 'text').toLowerCase()))
      continue
    if ((o.value ?? '') !== '') return true
  }
  return false
}

/**
 * Sau khi sw báo `co-ban-moi` và máy chủ xác nhận có bản khác: trang chỉ được TỰ tải lại khi tab đang ẨN và không dở việc gì.
 * Tab hiện, hoặc đang dở → chỉ hiện dải + nút Cập nhật (người bấm tự quyết).
 */
export function nenTuTaiLai(doc: TaiLieuDoDang): boolean {
  return doc.visibilityState === 'hidden' && !coViecDoDang(doc)
}

/**
 * Bật bộ kiểm. Trả hàm gỡ. Hỏi mỗi 5 phút khi tab hiện, cộng khi tab hiện lại và khi có mạng trở lại; hai lần hỏi
 * cách nhau ≥ 60 giây; không chồng yêu cầu. Dừng hẳn (gỡ hẹn giờ + người nghe) ngay khi đã báo.
 */
export function batKiemBanMoi(hen: Hen, mt: MoiTruongBanMoi): () => void {
  const chay = mt.vanTayChay
  if (chay === null) return () => undefined

  let ungVien: string | null = null
  let lanCuoi: number | null = null
  let dangHoi = false
  let daDung = false
  let gioXacNhan: unknown = null
  let nhip: unknown = null
  /** Đã thấy tab bị ẩn kể từ lần kiểm trước (máy ngủ / app nền): `dongHo` có thể không tính thời gian đó. */
  let daAn = false
  /** Hẹn giờ xác nhận đã tới hạn nhưng lúc đó tab đang ẩn: lần hiện lại sẽ xác nhận ngay. */
  let xacNhanDenHan = false

  const huyXacNhan = () => {
    if (gioXacNhan !== null) hen.huyGio(gioXacNhan)
    gioXacNhan = null
  }
  const dung = () => {
    if (daDung) return
    daDung = true
    huyXacNhan()
    if (nhip !== null) hen.huyLap(nhip)
    nhip = null
    mt.cuaSo.removeEventListener('online', khiCoMang)
    mt.taiLieu.removeEventListener('visibilitychange', khiDoiHienAn)
  }

  /** `boGianCach`: bỏ luật "cách ≥ 60 giây" cho lần này (đã chờ bằng hẹn giờ, hoặc vừa ngủ dậy). Không bỏ luật không chồng yêu cầu. */
  function hoi(boGianCach = false): void {
    if (daDung || dangHoi) return
    if (mt.taiLieu.visibilityState !== 'visible') return
    const bay = mt.dongHo()
    if (!boGianCach && lanCuoi !== null && bay - lanCuoi < GIAN_CACH_TOI_THIEU_MS) return
    lanCuoi = bay
    dangHoi = true
    huyXacNhan()
    void layVanTayMayChu(mt.fetch).then((lan) => {
      dangHoi = false
      if (daDung) return
      const buoc = buocKiem(chay, ungVien, lan)
      ungVien = buoc.ungVien
      if (buoc.hien) {
        dung()
        mt.khiCoBanMoi()
        return
      }
      if (ungVien !== null) {
        // Hẹn giờ đã chờ đủ 60 giây thật: không đo lại bằng `dongHo` (có thể đứng yên lúc máy ngủ).
        gioXacNhan = hen.datGio(() => {
          gioXacNhan = null
          if (mt.taiLieu.visibilityState !== 'visible') xacNhanDenHan = true
          else hoi(true)
        }, CHO_XAC_NHAN_MS)
      }
    })
  }

  function khiCoMang(): void {
    hoi()
  }

  /**
   * Tab ẩn → ghi nhận. Tab hiện lại sau khi đã ẩn → kiểm ngay, bỏ luật cách ≥ 60 giây (đồng hồ đo có thể không tính lúc máy ngủ),
   * nhưng CHỈ khi chưa có ứng viên đang chờ xác nhận (không thì hai lần xác nhận có thể sát nhau). Hẹn giờ xác nhận lỡ lúc ẩn thì làm nốt.
   */
  function khiDoiHienAn(): void {
    if (daDung) return
    if (mt.taiLieu.visibilityState !== 'visible') {
      daAn = true
      return
    }
    const boGianCach = (daAn && ungVien === null) || xacNhanDenHan
    daAn = false
    xacNhanDenHan = false
    hoi(boGianCach)
  }

  mt.cuaSo.addEventListener('online', khiCoMang)
  mt.taiLieu.addEventListener('visibilitychange', khiDoiHienAn)
  nhip = hen.lapLai(() => hoi(), CHU_KY_KIEM_MS)
  return dung
}
